import { useEffect, useRef, useState } from 'react';
import type { MessageDTO, SearchResponseDTO } from '@tc/contracts';
import { api } from '../api.ts';
import { jumpToMessage, useStore } from '../store.ts';
import { formatTime, useT, useUiLocale } from '../i18n.ts';
import { conversationTitle, displayName } from './common.tsx';

/** Both texts a reader can see for a message: their translation and the original. */
function visibleTexts(m: MessageDTO): string[] {
  const out: string[] = [];
  if (m.translation?.status === 'ready' && m.translation.body) out.push(m.translation.body);
  if (m.body) out.push(m.body);
  return out;
}

/** Excerpt around the first match (grapheme-safe), preferring the text the reader sees by default. */
export function excerpt(m: MessageDTO, query: string, radius = 60): string {
  const q = query.trim().toLowerCase();
  const texts = visibleTexts(m);
  const text = texts.find((t) => t.toLowerCase().includes(q)) ?? texts[0] ?? '';
  const chars = Array.from(text);
  const lower = Array.from(text.toLowerCase());
  const qChars = Array.from(q);
  let at = -1;
  outer: for (let i = 0; i + qChars.length <= lower.length; i++) {
    for (let j = 0; j < qChars.length; j++) if (lower[i + j] !== qChars[j]) continue outer;
    at = i;
    break;
  }
  if (at < 0 || chars.length <= radius * 2) return chars.slice(0, radius * 2).join('') + (chars.length > radius * 2 ? '…' : '');
  const start = Math.max(0, at - radius);
  const end = Math.min(chars.length, at + qChars.length + radius);
  return (start > 0 ? '…' : '') + chars.slice(start, end).join('') + (end < chars.length ? '…' : '');
}

/** Render text with every case-insensitive occurrence of `query` wrapped in <mark>. */
export function Highlighted({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'giu');
  const parts = text.split(re);
  return <>{parts.map((p, i) => (i % 2 === 1 ? <mark key={i}>{p}</mark> : p))}</>;
}

interface Props {
  query: string;
  conversationId?: string;
  /** Show the conversation name on each result (global search). */
  showConversation: boolean;
  onPicked?: () => void;
}

export function SearchResults({ query, conversationId, showConversation, onPicked }: Props) {
  const t = useT();
  const ui = useUiLocale();
  const convs = useStore((s) => s.conversations);
  const meId = useStore((s) => s.me?.id);
  const [results, setResults] = useState<MessageDTO[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState<string | null>(null);
  const reqId = useRef(0);

  const fetchPage = async (before?: string) => {
    const q = query.trim();
    if (!q) return;
    const id = ++reqId.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ q, limit: '50' });
      if (conversationId) params.set('conversationId', conversationId);
      if (before) params.set('before', before);
      const r = await api<SearchResponseDTO>('GET', `/api/search?${params}`);
      if (id !== reqId.current) return;
      setResults((prev) => (before ? [...prev, ...r.messages] : r.messages));
      setHasMore(r.hasMore);
    } catch {
      /* keep previous results */
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  };

  useEffect(() => {
    setResults([]);
    setHasMore(false);
    if (!query.trim()) return;
    const timer = setTimeout(() => void fetchPage(), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, conversationId]);

  if (!query.trim()) return null;
  return (
    <div className="search-results" role="region" aria-label={t('searchResults')} aria-busy={loading}>
      {!loading && results.length === 0 && <p className="empty">{t('noResults')}</p>}
      <ul role="list">
        {results.map((m) => {
          const c = convs[m.conversationId];
          return (
            <li key={m.id}>
              <button
                className="result"
                onClick={async () => {
                  setNotFound(null);
                  const ok = await jumpToMessage(m.conversationId, m.id);
                  if (!ok) setNotFound(m.id);
                  onPicked?.();
                }}
              >
                <span className="result-top">
                  <span className="result-who">
                    {showConversation && c && <span className="result-conv">{conversationTitle(c, meId)}</span>}
                    <span>{displayName(m.senderId, ui)}</span>
                  </span>
                  <time>{formatTime(m.createdAt, ui)}</time>
                </span>
                <span className="result-text">
                  <Highlighted text={excerpt(m, query)} query={query} />
                </span>
                {notFound === m.id && <span className="muted small">{t('searchTooOld')}</span>}
              </button>
            </li>
          );
        })}
      </ul>
      {hasMore && (
        <button className="btn ghost small more" disabled={loading} onClick={() => void fetchPage(results.at(-1)?.id)}>
          {t('showMore')}
        </button>
      )}
    </div>
  );
}
